import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw, Phone, Mail, Building2, Clock, X, Loader2, Download, Trash2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AdminSettings from "@/components/AdminSettings";

const STATUSES = ["new", "contacted", "referred", "closed"];

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const scoreClass = (s) => (s >= 8 ? "score-hi" : s >= 5 ? "score-mid" : "score-lo");

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("cases"); // "cases" | "consults"
  const [cases, setCases] = useState([]);
  const [consults, setConsults] = useState([]);
  const [stats, setStats] = useState({ total: 0, high_score: 0, new: 0, referred: 0, consults_paid: 0, consults_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", min_score: "" });
  const [open, setOpen] = useState(null); // selected case

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.min_score) params.min_score = filter.min_score;
      const [r1, r2, r3] = await Promise.all([
        api.get("/cases", { params }),
        api.get("/stats"),
        api.get("/consults"),
      ]);
      setCases(r1.data);
      setStats(r2.data);
      setConsults(r3.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const r = await api.patch(`/cases/${id}/status`, { status });
      setCases((p) => p.map((c) => (c.id === id ? r.data : c)));
      if (open?.id === id) setOpen(r.data);
      toast.success(`Status updated → ${status}`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const deleteCase = async (c) => {
    const label = c.full_name || c.email || c.id;
    if (!window.confirm(`Permanently delete the case from ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/cases/${c.id}`);
      setCases((p) => p.filter((x) => x.id !== c.id));
      if (open?.id === c.id) setOpen(null);
      setStats((s) => ({
        ...s,
        total: Math.max(0, (s.total || 0) - 1),
        high_score: c.ai_score >= 8 ? Math.max(0, (s.high_score || 0) - 1) : s.high_score,
        new: c.status === "new" ? Math.max(0, (s.new || 0) - 1) : s.new,
        referred: c.status === "referred" ? Math.max(0, (s.referred || 0) - 1) : s.referred,
      }));
      toast.success("Case deleted.");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const deleteConsult = async (c) => {
    const label = c.full_name || c.email || c.id;
    if (!window.confirm(`Permanently delete the consult from ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/consults/${c.id}`);
      setConsults((p) => p.filter((x) => x.id !== c.id));
      setStats((s) => ({
        ...s,
        consults_paid: c.payment_status === "paid" ? Math.max(0, (s.consults_paid || 0) - 1) : s.consults_paid,
        consults_revenue: c.payment_status === "paid" ? Math.max(0, (s.consults_revenue || 0) - Number(c.fee_amount || 0)) : s.consults_revenue,
      }));
      toast.success("Consult deleted.");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // Score distribution 1..10 from currently filtered cases
  const distribution = Array.from({ length: 10 }, (_, i) =>
    cases.filter((c) => c.ai_score === i + 1).length
  );
  const distMax = Math.max(1, ...distribution);
  const avgScore = cases.length
    ? (cases.reduce((s, c) => s + (c.ai_score || 0), 0) / cases.length).toFixed(1)
    : "—";

  const exportCsv = () => {
    if (!cases.length) {
      toast.info("No cases to export.");
      return;
    }
    const cols = [
      "id", "created_at", "ai_score", "status", "matter_type", "claim_type",
      "damages", "evidence", "urgency", "budget", "is_texas",
      "full_name", "email", "phone", "company", "callback_time",
      "firm_match", "ai_rationale", "description",
    ];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [cols.join(",")].concat(
      cases.map((c) => cols.map((k) => escape(c[k])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `tfm-cases-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${cases.length} case${cases.length === 1 ? "" : "s"} to CSV`);
  };

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <div className="admin-bar">
        <div className="brand">
          <div className="brand-mark">tx</div>
          <div>
            <div className="brand-name">TexasBusinessDispute.com</div>
            <div className="brand-sub">Internal · Cases dashboard</div>
          </div>
        </div>
        <div className="admin-tools">
          <Link to="/" className="btn-ghost" style={{ background: "transparent", color: "#e7dfd2", borderColor: "rgba(231,223,210,0.2)" }} data-testid="admin-view-site-btn">View site</Link>
          <button className="btn-ghost" onClick={load} style={{ background: "transparent", color: "#e7dfd2", borderColor: "rgba(231,223,210,0.2)" }} data-testid="admin-refresh-btn">
            <RefreshCw size={13} /> Refresh
          </button>
          <button className="btn-ghost" onClick={logout} style={{ background: "transparent", color: "#e7dfd2", borderColor: "rgba(231,223,210,0.2)" }} data-testid="admin-logout-btn">
            <LogOut size={13} /> Logout {user?.email ? `(${user.email})` : ""}
          </button>
        </div>
      </div>

      <div className="container-tx" style={{ paddingTop: 32, paddingBottom: 64 }}>
        <div className="eyebrow copper">Qualified lead database</div>
        <h2 className="serif" style={{ fontSize: 32, fontWeight: 600, margin: "8px 0 4px", letterSpacing: "-0.02em" }}>Cases</h2>
        <p style={{ color: "var(--muted)", fontSize: 13.5 }}>Each new submission is scored 1–10 for litigation viability. SMS lands on your phone instantly; cases scoring 8+ also land in your inbox.</p>

        <div className="admin-tabs" data-testid="admin-tabs">
          <button className={`admin-tab ${tab === "cases" ? "active" : ""}`} onClick={() => setTab("cases")} data-testid="tab-cases">
            Cases <span className="tab-count">{stats.total}</span>
          </button>
          <button className={`admin-tab ${tab === "consults" ? "active" : ""}`} onClick={() => setTab("consults")} data-testid="tab-consults">
            Paid consults <span className="tab-count">{stats.consults_paid}</span>
          </button>
          <button className={`admin-tab ${tab === "settings" ? "active" : ""}`} onClick={() => setTab("settings")} data-testid="tab-settings">
            Settings
          </button>
        </div>

        {tab !== "settings" && (
        <div className="admin-stats" data-testid="admin-stats">
          <div className="stat"><div className="lbl">Total cases</div><div className="val" data-testid="stat-total">{stats.total}</div></div>
          <div className="stat"><div className="lbl">High viability (≥8)</div><div className="val" style={{ color: "#b73b2a" }} data-testid="stat-high">{stats.high_score}</div></div>
          <div className="stat"><div className="lbl">Paid consults</div><div className="val" style={{ color: "var(--copper)" }} data-testid="stat-consults">{stats.consults_paid}</div></div>
          <div className="stat"><div className="lbl">Consult revenue</div><div className="val" style={{ color: "#2f5a36" }} data-testid="stat-consults-revenue">${(stats.consults_revenue || 0).toLocaleString()}</div></div>
        </div>
        )}

        {tab === "settings" && (<AdminSettings />)}

        {/* score distribution histogram */}
        {tab === "cases" && (
        <div className="dist-card" data-testid="score-distribution">
          <div className="dist-head">
            <div>
              <div className="eyebrow copper">Viability distribution</div>
              <div className="dist-sub">
                {cases.length} case{cases.length === 1 ? "" : "s"} in current view · average score{" "}
                <strong style={{ color: "var(--copper)", fontFamily: "var(--mono)", fontWeight: 600 }}>{avgScore}</strong>
              </div>
            </div>
          </div>
          <div className="dist-bars">
            {distribution.map((count, i) => {
              const score = i + 1;
              const pct = (count / distMax) * 100;
              const tone = score >= 8 ? "hi" : score >= 5 ? "mid" : "lo";
              return (
                <div className="dist-col" key={score} title={`Score ${score}/10 — ${count} case${count === 1 ? "" : "s"}`} data-testid={`dist-bar-${score}`}>
                  <div className="dist-count">{count || ""}</div>
                  <div className="dist-track">
                    <div className={`dist-fill dist-${tone}`} style={{ height: `${pct}%` }} />
                  </div>
                  <div className="dist-axis">{score}</div>
                </div>
              );
            })}
          </div>
          <div className="dist-legend">
            <span><i className="dot dist-lo" /> 1–4 low</span>
            <span><i className="dot dist-mid" /> 5–7 moderate</span>
            <span><i className="dot dist-hi" /> 8–10 high</span>
          </div>
        </div>
        )}

        {tab === "cases" && (<>
        {/* filters */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <select className="frow-select" value={filter.status} onChange={(e) => setFilter((p) => ({ ...p, status: e.target.value }))} style={{ padding: "8px 12px", border: "1px solid var(--rule)", background: "#fff", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink)", cursor: "pointer" }} data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
          <select value={filter.min_score} onChange={(e) => setFilter((p) => ({ ...p, min_score: e.target.value }))} style={{ padding: "8px 12px", border: "1px solid var(--rule)", background: "#fff", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink)", cursor: "pointer" }} data-testid="filter-score">
            <option value="">Any score</option>
            <option value="8">Score ≥ 8 (high)</option>
            <option value="5">Score ≥ 5</option>
            <option value="1">Score ≥ 1</option>
          </select>
          <button className="btn-copper" style={{ marginLeft: "auto", padding: "8px 16px", fontSize: 11 }} onClick={exportCsv} data-testid="export-csv-btn">
            <Download size={13} /> Export CSV
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 64, textAlign: "center", color: "var(--muted)" }}>
            <Loader2 size={20} className="animate-spin" style={{ display: "inline-block" }} />
            <div style={{ marginTop: 8, fontFamily: "var(--mono)", fontSize: 11, letterSpacing: ".14em" }}>LOADING CASES...</div>
          </div>
        ) : cases.length === 0 ? (
          <div style={{ padding: 64, textAlign: "center", color: "var(--faint)", background: "var(--paper)", border: "1px solid var(--rule)" }} data-testid="admin-empty">
            No cases yet. Submit one from the public landing page to test the flow.
          </div>
        ) : (
          <table className="case-table" data-testid="cases-table">
            <thead>
              <tr>
                <th>Score</th>
                <th>When</th>
                <th>Name</th>
                <th>Matter</th>
                <th>Damages</th>
                <th>Firm match</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} className="row" onClick={() => setOpen(c)} data-testid={`case-row-${c.id}`}>
                  <td><span className={`score-pill ${scoreClass(c.ai_score)}`} data-testid={`case-score-${c.id}`}>{c.ai_score}/10</span></td>
                  <td style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 11 }}>{fmtDate(c.created_at)}</td>
                  <td><div style={{ fontWeight: 600 }}>{c.full_name}</div><div style={{ color: "var(--muted)", fontSize: 12 }}>{c.email}</div></td>
                  <td style={{ fontSize: 12.5 }}>{c.matter_type}</td>
                  <td style={{ color: "var(--muted)", fontSize: 12 }}>{c.damages}</td>
                  <td style={{ fontSize: 12, color: "var(--ink-soft)" }}>{c.firm_match}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      value={c.status}
                      onChange={(e) => updateStatus(c.id, e.target.value)}
                      className={`status-pill s-${c.status}`}
                      style={{ border: "1px solid", padding: "4px 8px", cursor: "pointer", appearance: "none" }}
                      data-testid={`case-status-select-${c.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                    </select>
                  </td>
                  <td onClick={(e) => e.stopPropagation()} style={{ width: 36, textAlign: "right" }}>
                    <button
                      className="row-del"
                      title="Delete case"
                      onClick={() => deleteCase(c)}
                      data-testid={`case-delete-btn-${c.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </>)}

        {tab === "consults" && (
          <div data-testid="consults-panel">
            {loading ? (
              <div style={{ padding: 64, textAlign: "center", color: "var(--muted)" }}>
                <Loader2 size={20} className="animate-spin" style={{ display: "inline-block" }} />
                <div style={{ marginTop: 8, fontFamily: "var(--mono)", fontSize: 11, letterSpacing: ".14em" }}>LOADING CONSULTS...</div>
              </div>
            ) : consults.length === 0 ? (
              <div style={{ padding: 64, textAlign: "center", color: "var(--faint)", background: "var(--paper)", border: "1px solid var(--rule)" }} data-testid="consults-empty">
                No paid consultations yet. The "Book paid consult — $95" button on the landing page will route bookings here once Stripe checkout completes.
              </div>
            ) : (
              <table className="case-table" data-testid="consults-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>When</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Matter brief</th>
                    <th>Preferred</th>
                    <th>Fee</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {consults.map((c) => (
                    <tr key={c.id} data-testid={`consult-row-${c.id}`}>
                      <td>
                        <span className={`status-pill ${c.payment_status === "paid" ? "s-referred" : c.payment_status === "pending" ? "s-new" : "s-closed"}`}>
                          {c.payment_status?.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 11 }}>{fmtDate(c.created_at)}</td>
                      <td><div style={{ fontWeight: 600 }}>{c.full_name}</div><div style={{ color: "var(--muted)", fontSize: 12 }}>{c.email}</div></td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{c.phone}</td>
                      <td style={{ fontSize: 12, color: "var(--ink-soft)", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.matter_brief}</td>
                      <td style={{ fontSize: 12 }}>{c.preferred_time || "—"}</td>
                      <td style={{ fontFamily: "var(--mono)", fontWeight: 600, color: c.payment_status === "paid" ? "#2f5a36" : "var(--muted)" }}>${Number(c.fee_amount).toFixed(0)}</td>
                      <td style={{ width: 36, textAlign: "right" }}>
                        <button
                          className="row-del"
                          title="Delete consult"
                          onClick={() => deleteConsult(c)}
                          data-testid={`consult-delete-btn-${c.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {open && (
        <>
          <div className="drawer-bg" onClick={() => setOpen(null)} />
          <aside className="drawer" data-testid="case-drawer">
            <div className="drawer-head">
              <div>
                <div className="intake-eyebrow">Case · {fmtDate(open.created_at)}</div>
                <h3 className="serif" style={{ fontSize: 22, fontWeight: 600, margin: "6px 0 0", letterSpacing: "-0.015em" }}>{open.full_name}</h3>
                <div style={{ color: "var(--muted)", fontSize: 12.5, marginTop: 2 }}>{open.matter_type}</div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span className={`score-pill ${scoreClass(open.ai_score)}`}>{open.ai_score}/10</span>
                <button
                  className="btn-ghost"
                  onClick={() => deleteCase(open)}
                  title="Delete case"
                  style={{ padding: 8, borderColor: "#e0c2bb", color: "#b73b2a" }}
                  data-testid="case-drawer-delete"
                >
                  <Trash2 size={14} />
                </button>
                <button className="btn-ghost" onClick={() => setOpen(null)} style={{ padding: 8 }} data-testid="case-drawer-close"><X size={14} /></button>
              </div>
            </div>
            <div className="drawer-body">
              <div className="kv">
                <div className="k">Phone</div><div className="v"><Phone size={12} style={{ display: "inline", marginRight: 6, color: "var(--copper)" }} />{open.phone}</div>
                <div className="k">Email</div><div className="v"><Mail size={12} style={{ display: "inline", marginRight: 6, color: "var(--copper)" }} />{open.email}</div>
                {open.company && (<><div className="k">Company</div><div className="v"><Building2 size={12} style={{ display: "inline", marginRight: 6, color: "var(--copper)" }} />{open.company}</div></>)}
                {open.callback_time && (<><div className="k">Callback</div><div className="v"><Clock size={12} style={{ display: "inline", marginRight: 6, color: "var(--copper)" }} />{open.callback_time}</div></>)}
              </div>

              <div style={{ borderTop: "1px solid var(--rule)" }} />

              <div className="kv">
                <div className="k">TX party</div><div className="v">{open.is_texas === "yes" ? "Yes — Texas-based" : "No"}</div>
                <div className="k">Damages</div><div className="v">{open.damages}</div>
                <div className="k">Evidence</div><div className="v">{open.evidence}</div>
                <div className="k">Urgency</div><div className="v">{open.urgency}</div>
                <div className="k">Budget</div><div className="v">{open.budget}</div>
              </div>

              <div style={{ borderTop: "1px solid var(--rule)" }} />

              <div>
                <div className="intake-eyebrow">AI Assessment</div>
                <div className="kv" style={{ marginTop: 8 }}>
                  <div className="k">Claim type</div><div className="v">{open.claim_type}</div>
                  <div className="k">Firm match</div><div className="v">{open.firm_match}</div>
                </div>
                <div style={{ marginTop: 12, padding: 14, background: "#fff8f1", borderLeft: "2px solid var(--copper)", fontSize: 13, lineHeight: 1.7, color: "var(--ink-soft)" }}>{open.ai_rationale}</div>
              </div>

              <div>
                <div className="intake-eyebrow">Description</div>
                <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap", color: "var(--ink-soft)" }}>{open.description}</div>
              </div>

              <div>
                <div className="intake-eyebrow">Status</div>
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(open.id, s)}
                      className={`status-pill s-${s}`}
                      style={{ cursor: "pointer", padding: "6px 12px", fontSize: 11, opacity: open.status === s ? 1 : 0.55 }}
                      data-testid={`drawer-status-${s}`}
                    >
                      {s.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
